import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import { downloadCompletionBadge } from '../utils/downloadCmsAsset';
import '../styles/course-completion-modal.css';

interface CourseCompletionModalProps {
    isOpen: boolean;
    onClose: () => void;
    modalHeading?: string | null;
    modalImageUrl?: string | null;
    modalContent?: string | null;
    modalBadgeUrl?: string | null;
    modalDownloadUrl?: string | null;
    modalCloseLabel?: string | null;
    modalLinkText?: string | null;
    modalIconUrl?: string | null;
}

export default function CourseCompletionModal({
    isOpen,
    onClose,
    modalHeading,
    modalImageUrl,
    modalContent,
    modalBadgeUrl,
    modalDownloadUrl,
    modalCloseLabel,
    modalLinkText,
    modalIconUrl
}: CourseCompletionModalProps) {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    if (!isOpen) return null;

    // Use dynamic content if provided, otherwise fall back to defaults
    const heading = modalHeading || "🎉 Congratulations! You've completed this course";
    const content = modalContent || "You've officially finished your Polaris Athlete course and earned your digital achievement badge.";
    const closeLabel = modalCloseLabel || "Close";
    const linkText = modalLinkText || "DOWNLOAD BADGE ⬇";

    const handleDownloadBadge = async () => {
        setDownloadError(null);
        setIsDownloading(true);
        try {
            await downloadCompletionBadge(modalDownloadUrl, modalBadgeUrl);
        } catch (error) {
            setDownloadError(
                error instanceof Error ? error.message : "Unable to download badge. Please try again."
            );
        } finally {
            setIsDownloading(false);
        }
    };

    return createPortal(
        <div className="modal-overlay">
            <div className="course-completion-modal-content">
                <button className="modal-close-btn" onClick={onClose}>
                    ×
                </button>

                <div className="course-completion-body">
                    <h1 className="completion-title">
                        {heading}
                    </h1>

                    <div className="completion-content">
                        {modalImageUrl && (
                            <div className="completion-image">
                                <img src={modalImageUrl} alt="Completion" />
                            </div>
                        )}

                        <div className="completion-info">
                            {modalContent ? (
                                <div className="completion-message" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
                            ) : (
                                <>
                                    <p className="completion-message">
                                        {content}
                                    </p>

                                    <div className="next-steps">
                                        <h3>Next steps:</h3>
                                        <ul>
                                            <li>
                                                <strong>Download your badge</strong> to showcase your accomplishment
                                            </li>
                                            <li>
                                                Add it to your <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="linkedin-link">LinkedIn profile</a> to highlight your growth and commitment
                                            </li>
                                        </ul>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="course-completion-footer">
                    {downloadError && (
                        <p className="course-completion-download-error" role="alert">
                            {downloadError}
                        </p>
                    )}
                    <button
                        className="download-badge-btn"
                        onClick={handleDownloadBadge}
                        disabled={isDownloading}
                    >
                        {modalIconUrl && <img src={modalIconUrl} alt="Icon" className="badge-icon" />}
                        {isDownloading ? "Downloading..." : linkText}
                    </button>
                    <button className="close-completion-btn" onClick={onClose}>
                        {closeLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
